import { Permissions } from '@lagerfeuer/shared';
import { useCallback, useMemo, type ComponentType } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { SettingsLayout, type SettingsSection } from '../../components/SettingsLayout';
import { useDocumentTitle } from '../../lib/documentTitle';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { extraMenuItems } from '../extras';
import { BansTab } from './BansTab';
import { EmojisTab, SoundsTab, StickersTab } from './ExpressionsTabs';
import { hasPerm, useActor, type Actor } from './hierarchy';
import { InvitesTab } from './InvitesTab';
import { MembersTab } from './MembersTab';
import { OverviewTab } from './OverviewTab';
import { DeleteServerTab, TransferTab } from './OwnershipTabs';
import { RolesTab } from './RolesTab';
import './server-settings.css';

interface TabDef extends SettingsSection {
  allowed: (a: Actor) => boolean;
  component: ComponentType<{ serverId: string; onClose?: () => void }>;
  /** Erweiterung ohne eingebettete Komponente: Navigation zu diesem Pfad. */
  to?: string;
}

const NoContent = () => null;

const P = Permissions;

const TABS: TabDef[] = [
  { id: 'uebersicht', label: 'Übersicht', group: 'Server', allowed: (a) => hasPerm(a, P.MANAGE_SERVER), component: OverviewTab },
  { id: 'rollen', label: 'Rollen', group: 'Server', allowed: (a) => hasPerm(a, P.MANAGE_ROLES), component: RolesTab },
  { id: 'emojis', label: 'Emojis', group: 'Server', allowed: (a) => hasPerm(a, P.MANAGE_EMOJIS), component: EmojisTab },
  { id: 'sticker', label: 'Sticker', group: 'Server', allowed: (a) => hasPerm(a, P.MANAGE_EMOJIS), component: StickersTab },
  { id: 'sounds', label: 'Sounds', group: 'Server', allowed: (a) => hasPerm(a, P.MANAGE_EMOJIS), component: SoundsTab },
  {
    id: 'mitglieder',
    label: 'Mitglieder',
    group: 'Mitglieder',
    allowed: (a) => [P.KICK_MEMBERS, P.BAN_MEMBERS, P.MANAGE_ROLES, P.MANAGE_NICKNAMES, P.MODERATE_MEMBERS].some((b) => hasPerm(a, b)),
    component: MembersTab,
  },
  { id: 'einladungen', label: 'Einladungen', group: 'Mitglieder', allowed: (a) => hasPerm(a, P.MANAGE_SERVER), component: InvitesTab },
  { id: 'baenne', label: 'Banns', group: 'Mitglieder', allowed: (a) => hasPerm(a, P.BAN_MEMBERS), component: BansTab },
];

const OWNER_TABS: TabDef[] = [
  { id: 'besitz', label: 'Besitz übertragen', allowed: (a) => a.isOwner, component: TransferTab, danger: true },
  { id: 'loeschen', label: 'Server löschen', allowed: (a) => a.isOwner, component: DeleteServerTab, danger: true },
];

/** Vollbild-Servereinstellungen `/servers/:serverId/settings/:tab`. Tabs nur mit passenden Rechten. */
export function ServerSettings() {
  const { serverId = '', tab } = useParams();
  const navigate = useNavigate();
  const server = useAppStore((s) => s.servers[serverId]);
  const actor = useActor(serverId);

  const tabs = useMemo<TabDef[]>(() => {
    if (!actor) return [];
    const base = TABS.filter((t) => t.allowed(actor));
    // Erweiterungs-Tabs: mit eigenem Recht (z. B. Webhooks → MANAGE_WEBHOOKS, R7b) nur mit diesem, sonst wie bisher
    // mit irgendeinem Verwaltungsrecht.
    const extras: TabDef[] = extraMenuItems
      .filter((i) => i.slot === 'serverSettings' && (i.component || i.to))
      .filter((i) => (i.serverPermission !== undefined ? hasPerm(actor, i.serverPermission) : base.length > 0 || actor.isOwner))
      .map((i) => ({
        id: `extra-${i.id}`,
        label: i.label,
        group: 'Erweiterungen',
        allowed: () => true,
        component: (i.component ?? NoContent) as TabDef['component'],
        ...(!i.component && i.to ? { to: i.to.replace(':serverId', serverId) } : {}),
      }));
    const all = [...base, ...extras, ...OWNER_TABS.filter((t) => t.allowed(actor))];
    return all.map((t) => ({ ...t, testId: `server-settings-tab-${t.id}` }));
  }, [actor, serverId]);

  const close = useCallback(() => void navigate(server ? paths.server(serverId) : paths.home()), [navigate, server, serverId]);
  const select = useCallback(
    (id: string) => {
      const to = tabs.find((t) => t.id === id)?.to;
      void navigate(to ?? paths.serverSettings(serverId, id), to ? undefined : { replace: true });
    },
    [navigate, serverId, tabs],
  );

  const active = tabs.find((t) => t.id === tab && !t.to) ?? tabs.find((t) => !t.to);

  useDocumentTitle(server ? `Servereinstellungen – ${server.name}` : null);

  if (!server || !actor) return <Navigate to={paths.home()} replace />;
  if (!active) {
    return (
      <SettingsLayout title={server.name} sections={[]} active="" onSelect={select} onClose={close} testId="server-settings">
        <p className="muted" data-testid="server-settings-forbidden">
          Du hast keine Rechte, diesen Server zu verwalten.
        </p>
      </SettingsLayout>
    );
  }
  if (tab !== active.id) return <Navigate to={paths.serverSettings(serverId, active.id)} replace />;
  const Tab = active.component;
  return (
    <SettingsLayout title={server.name} sections={tabs} active={active.id} onSelect={select} onClose={close} testId="server-settings">
      <div data-testid={`server-settings-panel-${active.id}`}>
        <Tab serverId={serverId} onClose={close} />
      </div>
    </SettingsLayout>
  );
}
